import React, { useEffect, useState } from 'react';
import {
  Flag, Loader2, ChevronDown, ChevronUp, MessageSquare,
  Check, X, AlertTriangle, ShieldAlert, Eye,
} from 'lucide-react';
import { useAdminStore } from '../../store/adminStore';
import api from '../../lib/api';

/* ── Reason labels ── */
const REASON_LABELS: Record<string, string> = {
  harassment: 'Harcèlement',
  spam: 'Spam',
  hate_speech: 'Discours haineux',
  nsfw: 'Contenu NSFW',
  threats: 'Menaces',
  doxxing: 'Doxxing',
  impersonation: 'Usurpation d\'identité',
  illegal: 'Activité illégale',
  other: 'Autre',
};

const STATUS_STYLES: Record<string, { label: string; cls: string }> = {
  pending: { label: 'En attente', cls: 'bg-warning/10 text-warning' },
  reviewed: { label: 'Traité', cls: 'bg-success/10 text-success' },
  dismissed: { label: 'Rejeté', cls: 'bg-text-muted/10 text-text-muted' },
};

const AdminReports: React.FC = () => {
  const { reports, isLoadingReports, fetchReports, updateReport, addModerationAction, setSection } = useAdminStore();
  const [filter, setFilter] = useState('pending');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [context, setContext] = useState<any[]>([]);
  const [loadingContext, setLoadingContext] = useState(false);
  const [quickAction, setQuickAction] = useState<string>('');
  const [quickReason, setQuickReason] = useState('');

  useEffect(() => { fetchReports(filter); }, [filter]);

  const loadContext = async (reportId: string) => {
    setLoadingContext(true);
    try {
      const { data } = await api.get(`/admin/reports/${reportId}/context`);
      setContext(data.messages || data);
    } catch { setContext([]); }
    setLoadingContext(false);
  };

  const handleExpand = (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      loadContext(id);
    }
  };

  const handleResolve = async (id: string, status: 'reviewed' | 'dismissed', note?: string) => {
    await updateReport(id, status, note);
    setExpandedId(null);
  };

  const handleQuickModerate = async (reportId: string, userId: string) => {
    if (!quickAction || !quickReason.trim()) return;
    const ok = await addModerationAction(userId, {
      actionType: quickAction,
      reason: quickReason,
    });
    if (ok) {
      await handleResolve(reportId, 'reviewed', `Action: ${quickAction}`);
      setQuickAction('');
      setQuickReason('');
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-text-primary">Signalements</h2>
            <p className="text-xs text-text-muted mt-0.5">File de modération — traitez les signalements un par un</p>
          </div>
          <div className="flex gap-1 bg-bg-tertiary/50 rounded-lg p-0.5">
            {['pending', 'reviewed', 'dismissed'].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  filter === s ? 'bg-bg-secondary text-text-primary shadow-sm' : 'text-text-muted hover:text-text-secondary'
                }`}
              >
                {STATUS_STYLES[s].label}
                {s === 'pending' && reports.length > 0 && filter === 'pending' && (
                  <span className="ml-1.5 bg-error text-white text-[9px] rounded-full px-1.5">{reports.length}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {isLoadingReports ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-admin-accent" />
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-16">
            <Flag className="h-10 w-10 text-text-muted/20 mx-auto mb-3" />
            <p className="text-sm text-text-muted">Aucun signalement {filter === 'pending' ? 'en attente' : ''}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {reports.map((r) => {
              const isExpanded = expandedId === r.id;
              const s = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
              return (
                <div key={r.id} className="bg-bg-secondary/60 rounded-xl border border-border-color/50 overflow-hidden">
                  <button
                    onClick={() => handleExpand(r.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-highlight/20 transition-colors"
                  >
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${r.status === 'pending' ? 'bg-warning' : r.status === 'reviewed' ? 'bg-success' : 'bg-text-muted/30'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-text-primary">@{r.reported_username}</span>
                        <span className="text-text-muted">signalé par</span>
                        <span className="text-text-secondary">@{r.reporter_username}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs bg-bg-tertiary text-text-secondary px-1.5 py-0.5 rounded">
                          {REASON_LABELS[r.reason] || r.reason}
                        </span>
                        <span className="text-[10px] text-text-muted">{new Date(r.created_at).toLocaleDateString('fr-CA')}</span>
                        {r.server_name && <span className="text-[10px] text-text-muted">· {r.server_name}</span>}
                      </div>
                    </div>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${s.cls}`}>{s.label}</span>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-text-muted" /> : <ChevronDown className="h-4 w-4 text-text-muted" />}
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-3 border-t border-border-color/30 pt-3">
                      {/* Details */}
                      {r.details && (
                        <div className="bg-bg-tertiary/50 rounded-lg p-3">
                          <p className="text-xs text-text-muted mb-1">Détails du signalement:</p>
                          <p className="text-sm text-text-secondary">{r.details}</p>
                        </div>
                      )}

                      {/* Reported message */}
                      {r.message_content && (
                        <div className="bg-error/5 border border-error/20 rounded-lg p-3">
                          <p className="text-xs text-error/60 mb-1 flex items-center gap-1"><MessageSquare className="h-3 w-3" /> Message signalé:</p>
                          <p className="text-sm text-text-primary">{r.message_content}</p>
                        </div>
                      )}

                      {/* Message context */}
                      {loadingContext ? (
                        <Loader2 className="h-4 w-4 animate-spin text-text-muted" />
                      ) : context.length > 0 && (
                        <div className="bg-bg-tertiary/30 rounded-lg p-3 space-y-2">
                          <p className="text-xs text-text-muted mb-1 flex items-center gap-1"><Eye className="h-3 w-3" /> Contexte:</p>
                          {context.map((m: any, i: number) => (
                            <div key={i} className={`text-xs ${m.id === r.target_id ? 'bg-warning/10 rounded px-2 py-1' : ''}`}>
                              <span className="font-medium text-text-secondary">{m.username}: </span>
                              <span className="text-text-secondary/80">{m.content}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Quick moderation */}
                      {r.status === 'pending' && (
                        <div className="space-y-2">
                          <div className="flex gap-2 flex-wrap">
                            {['warning', 'violation', 'temp_suspension', 'ban'].map((a) => (
                              <button
                                key={a}
                                onClick={() => setQuickAction(quickAction === a ? '' : a)}
                                className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
                                  quickAction === a ? 'border-admin-accent bg-admin-accent/10 text-admin-accent' : 'border-border-color text-text-secondary hover:border-text-muted'
                                }`}
                              >
                                {a === 'warning' ? '⚠ Warn' : a === 'violation' ? '🚫 Violation' : a === 'temp_suspension' ? '⏸ Suspendre' : '🔨 Ban'}
                              </button>
                            ))}
                          </div>
                          {quickAction && (
                            <div className="flex gap-2">
                              <input
                                value={quickReason}
                                onChange={(e) => setQuickReason(e.target.value)}
                                placeholder="Raison..."
                                className="flex-1 px-3 py-1.5 rounded-lg bg-bg-tertiary border border-border-color text-xs text-text-primary placeholder-text-muted"
                              />
                              <button
                                onClick={() => handleQuickModerate(r.id, r.reported_user_id)}
                                disabled={!quickReason.trim()}
                                className="px-3 py-1.5 rounded-lg bg-error text-white text-xs font-medium disabled:opacity-50"
                              >
                                Appliquer & résoudre
                              </button>
                            </div>
                          )}
                          <div className="flex gap-2 pt-2">
                            <button
                              onClick={() => handleResolve(r.id, 'reviewed')}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-success/10 text-success text-xs font-medium hover:bg-success/20 transition-colors"
                            >
                              <Check className="h-3 w-3" /> Marquer traité
                            </button>
                            <button
                              onClick={() => handleResolve(r.id, 'dismissed')}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-text-muted/10 text-text-muted text-xs font-medium hover:bg-text-muted/20 transition-colors"
                            >
                              <X className="h-3 w-3" /> Rejeter
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReports;
